import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { TableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/TableColumnSettingsProvider";

import {
  ClientNotificationFilterForm,
  ClientNotificationFilterFormRef,
  ClientNotificationFilterFormValues,
} from "@components/Forms/ClientNotificationFilterForm/ClientNotificationFilterForm";
import { ClientNotificationsTable } from "@components/Tables";
import {
  ClientNotificationsTableConfig,
  areFiltersActive,
  getDefaultFilterValues,
} from "@components/Tables/ClientNotificationsTable/ClientNotificationsTableConfig";
import { ClientNotificationsTableColumnCustomization } from "@components/Tables/ClientNotificationsTable/UI/ClientNotificationsTableColumnCustomization";
import {
  DEFAULT_CLIENT_NOTIFICATION_TABLE_COLUMN_ORDER,
  DEFAULT_CLIENT_NOTIFICATION_TABLE_COLUMN_SETTINGS,
} from "@components/Tables/ClientNotificationsTable/UI/ClientNotificationsTableColumns";
import { useClientNotificationTableExport } from "@components/Tables/ClientNotificationsTable/utils/useClientNotificationTableExport";

import { Box } from "@design-system/Box/Box";
import { Card } from "@design-system/Card/Card";
import { IconButton } from "@design-system/IconButton/IconButton";
import { SlideInPanel } from "@design-system/SlideInPanel";
import { Text } from "@design-system/Text/Text";

type Props = {
  clientId: string;
  initialPageIndex?: number;
};

export const ClientNotificationsCard = ({
  clientId,
  initialPageIndex = 0,
}: Props) => {
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false);
  const [filterValues, setFilterValues] =
    useState<ClientNotificationFilterFormValues>(getDefaultFilterValues());

  const filterFormRef = useRef<ClientNotificationFilterFormRef>(null);

  const filtersActive = useMemo(() => {
    return areFiltersActive(filterValues);
  }, [filterValues]);

  const handleFilterClick = useCallback(() => {
    setIsFilterPanelOpen(true);
  }, []);

  const handleCloseFilterPanel = useCallback(() => {
    setIsFilterPanelOpen(false);
  }, []);

  const handleFiltersReset = useCallback(() => {
    setFilterValues(getDefaultFilterValues());
  }, []);

  const handleFilterChange = useCallback(
    (newValues: ClientNotificationFilterFormValues) => {
      setFilterValues(newValues);
      // Close the panel when form is submitted (e.g., via Enter key)
      setIsFilterPanelOpen(false);
    },
    [],
  );

  const handleApplyFilters = useCallback(() => {
    // Get current values from the form and apply them
    if (filterFormRef.current) {
      const currentFormValues = filterFormRef.current.getCurrentValues();
      setFilterValues(currentFormValues);
    }
  }, []);

  const handleColumnSettingsClick = useCallback(() => {
    setIsColumnSettingsOpen(true);
  }, []);

  const handleCloseColumnSettings = useCallback(() => {
    setIsColumnSettingsOpen(false);
  }, []);

  const { exportData, isExporting } = useClientNotificationTableExport({
    tableId: ClientNotificationsTableConfig.tableId,
    clientId,
    exportType: "csv",
    notificationType: filterValues.notificationType,
    applicationNameSearch: filterValues.applicationNameSearch,
  });

  const handleExportToCsv = useCallback(async () => {
    try {
      await exportData();
      toast.success("Notifications exported successfully!");
    } catch (error) {
      toast.error("Failed to export notifications");
      console.error("Export error:", error);
    }
  }, [exportData]);

  return (
    <TableColumnSettingsProvider
      tableId={ClientNotificationsTableConfig.tableId}
      defaultColumnSettings={DEFAULT_CLIENT_NOTIFICATION_TABLE_COLUMN_SETTINGS}
      defaultColumnOrder={DEFAULT_CLIENT_NOTIFICATION_TABLE_COLUMN_ORDER}
    >
      <Card grow>
        <Card.Header withBottomPadding>
          <Card.Header.Title>Notifications list</Card.Header.Title>
          <Card.Header.Actions>
            <Box direction="row" gap={8} align="center">
              {filtersActive && (
                <Text size={12} color={"textSecondary"}>
                  Filters are applied
                </Text>
              )}
              <IconButton
                size="sm"
                icon="faFilter"
                onClick={handleFilterClick}
                outline={"sm"}
                showBubble={filtersActive}
                tooltip="Show filters"
              />
              <IconButton
                size="sm"
                icon="faTableColumns"
                onClick={handleColumnSettingsClick}
                outline={"sm"}
                tooltip="Show/Hide columns"
              />
              <IconButton
                size="sm"
                icon={isExporting ? "faArrowsSpin" : "faFileCsv"}
                onClick={handleExportToCsv}
                outline={"sm"}
                tooltip={isExporting ? "Exporting..." : "Export table data to CSV"}
                disabled={isExporting}
              />
            </Box>
          </Card.Header.Actions>
        </Card.Header>
        <Card.Body noPadding grow>
          <ClientNotificationsTable
            initialPageIndex={initialPageIndex}
            clientId={clientId}
            {...(filterValues.applicationNameSearch &&
              filterValues.applicationNameSearch.trim() && {
                applicationNameSearch: filterValues.applicationNameSearch,
              })}
            {...(filterValues.notificationType &&
              filterValues.notificationType.length > 0 && {
                notificationType: filterValues.notificationType,
              })}
          />
        </Card.Body>
      </Card>

      <SlideInPanel
        isOpen={isFilterPanelOpen}
        onClose={handleCloseFilterPanel}
        onCancel={handleFiltersReset}
        onApply={handleApplyFilters}
        cancelButtonText={filtersActive ? "Clear filters" : undefined}
        applyButtonText="Apply filters"
        title="Notification Filters"
      >
        <ClientNotificationFilterForm
          ref={filterFormRef}
          onFilterChange={handleFilterChange}
          values={filterValues}
          errorsAPI={undefined}
        />
      </SlideInPanel>

      <ClientNotificationsTableColumnCustomization
        isOpen={isColumnSettingsOpen}
        onClose={handleCloseColumnSettings}
      />
    </TableColumnSettingsProvider>
  );
};
